/**
 * 前端权限检查工具
 * 用于检查用户是否拥有特定权限或角色
 */

/**
 * 检查用户是否拥有指定权限
 * @param {String} permissionCode - 权限编码（如：'user:add', 'user:edit'）
 * @returns {Boolean} - 是否拥有该权限
 */
export function hasPermission(permissionCode) {
    if (!permissionCode) {
        return true; // 没有指定权限要求，默认允许
    }
    
    try {
        // 从 localStorage 获取用户权限列表
        const permissionsStr = localStorage.getItem('user_permissions');
        if (!permissionsStr) {
            return false;
        }
        
        const permissions = JSON.parse(permissionsStr);
        
        // 检查是否是管理员（admin角色拥有所有权限）
        const rolesStr = localStorage.getItem('user_roles');
        if (rolesStr) {
            const roles = JSON.parse(rolesStr);
            const isAdmin = roles.some(role => role.role_code === 'admin');
            if (isAdmin) {
                return true; // 管理员拥有所有权限
            }
        }
        
        // 检查权限列表中是否包含指定权限编码
        return permissions.some(permission => permission.code === permissionCode);
    } catch (error) {
        console.error('权限检查失败:', error);
        return false;
    }
}

/**
 * 检查用户是否拥有任意一个权限（OR 逻辑）
 * @param {Array<String>} permissionCodes - 权限编码数组
 * @returns {Boolean} - 是否拥有任意一个权限
 * 
 * 示例：hasAnyPermission(['user:edit', 'user:delete']) 
 *      用户只要有编辑或删除权限之一即可
 */
export function hasAnyPermission(permissionCodes) {
    if (!permissionCodes || permissionCodes.length === 0) {
        return true;
    }
    
    return permissionCodes.some(code => hasPermission(code));
}

/**
 * 检查用户是否拥有所有权限（AND 逻辑）
 * @param {Array<String>} permissionCodes - 权限编码数组
 * @returns {Boolean} - 是否拥有所有权限
 * 
 * 示例：hasAllPermissions(['user:edit', 'user:delete'])
 *      用户必须同时拥有编辑和删除权限
 */
export function hasAllPermissions(permissionCodes) {
    if (!permissionCodes || permissionCodes.length === 0) {
        return true;
    }
    
    return permissionCodes.every(code => hasPermission(code));
}

/**
 * 检查用户是否拥有指定角色
 * @param {String} roleCode - 角色编码（如：'admin', 'manager'）
 * @returns {Boolean} - 是否拥有该角色
 */
export function hasRole(roleCode) {
    if (!roleCode) {
        return true;
    }
    
    try {
        const rolesStr = localStorage.getItem('user_roles');
        if (!rolesStr) {
            return false;
        }
        
        const roles = JSON.parse(rolesStr);
        return roles.some(role => role.role_code === roleCode);
    } catch (error) {
        console.error('角色检查失败:', error);
        return false;
    }
}

/**
 * 检查用户是否拥有任意一个角色（OR 逻辑）
 * @param {Array<String>} roleCodes - 角色编码数组
 * @returns {Boolean} - 是否拥有任意一个角色
 */
export function hasAnyRole(roleCodes) {
    if (!roleCodes || roleCodes.length === 0) {
        return true;
    }
    
    return roleCodes.some(code => hasRole(code));
}

/**
 * 检查用户是否拥有所有角色（AND 逻辑）
 * @param {Array<String>} roleCodes - 角色编码数组
 * @returns {Boolean} - 是否拥有所有角色
 */
export function hasAllRoles(roleCodes) {
    if (!roleCodes || roleCodes.length === 0) {
        return true;
    }
    
    return roleCodes.every(code => hasRole(code));
}

/**
 * 检查用户是否是管理员
 * @returns {Boolean} - 是否是管理员
 */
export function isAdmin() {
    return hasRole('admin');
}

/**
 * 获取当前用户的所有权限编码
 * @returns {Array<String>} - 权限编码数组
 */
export function getUserPermissionCodes() {
    try {
        const permissionsStr = localStorage.getItem('user_permissions');
        if (!permissionsStr) {
            return [];
        }
        
        const permissions = JSON.parse(permissionsStr);
        return permissions.map(p => p.code);
    } catch (error) {
        console.error('获取权限编码失败:', error);
        return [];
    }
}

/**
 * 获取当前用户的所有角色编码
 * @returns {Array<String>} - 角色编码数组
 */
export function getUserRoleCodes() {
    try {
        const rolesStr = localStorage.getItem('user_roles');
        if (!rolesStr) {
            return [];
        }
        
        const roles = JSON.parse(rolesStr);
        return roles.map(r => r.role_code);
    } catch (error) {
        console.error('获取角色编码失败:', error);
        return [];
    }
}

